import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../components/text/Text';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import PressableScale from './PressableScale';

/**
 * PrimaryButton: the one main action of a screen (pill, 52 high).
 * Props:
 * - label (string), onPress
 * - disabled, loading (spinner, ignores presses)
 * - icon: Feather icon name shown before the label (e.g. 'lock' on a locked action)
 * - size: 'regular' (52) | 'compact' (48, inside cards)
 * - tone: 'primary' | 'danger'
 * - accessibilityLabel, accessibilityHint, style
 */
const PrimaryButton = ({
	label,
	onPress,
	disabled = false,
	loading = false,
	icon,
	size = 'regular',
	tone = 'primary',
	accessibilityLabel,
	accessibilityHint,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui } } = useTheme();
	const isInactive = disabled || loading;
	const contentColor = disabled ? ui.colors.disabledText : ui.colors.onPrimary;

	return (
		<PressableScale
			onPress={isInactive ? undefined : onPress}
			disabled={isInactive}
			accessibilityRole="button"
			accessibilityLabel={accessibilityLabel || label}
			accessibilityHint={accessibilityHint}
			accessibilityState={{ disabled, busy: loading }}
			style={({ pressed }) => [
				styles.button,
				size === 'compact' && styles.compact,
				tone === 'danger' && styles.danger,
				disabled && styles.disabled,
				pressed && styles.pressed,
				style,
			]}
		>
			{
				loading ? (
					<ActivityIndicator color={contentColor} />
				) : (
					<View style={styles.content}>
						{Boolean(icon) && <Feather name={icon} size={18} color={contentColor} />}
						<Text style={[styles.label, disabled && styles.labelDisabled]} numberOfLines={2}>{label}</Text>
					</View>
				)
			}
		</PressableScale>
	);
};

const createStyles = ({ ui: { colors, radius, touch, type, space } }) => ({
	button: {
		minHeight: touch.primaryButton,
		borderRadius: radius.pill,
		backgroundColor: colors.primary,
		alignItems: 'center',
		justifyContent: 'center',
		paddingHorizontal: space.xl,
		paddingVertical: space.sm,
	},
	compact: {
		minHeight: touch.compactButton,
	},
	danger: {
		backgroundColor: colors.dangerSolid,
	},
	// The outline keeps a disabled button visible where disabledBg matches the card (Light);
	// the opacity fades it so it never reads as an available secondary button.
	disabled: {
		backgroundColor: colors.disabledBg,
		borderWidth: 1,
		borderColor: colors.borderStrong,
		opacity: 0.5,
	},
	pressed: {
		opacity: 0.85,
	},
	content: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		gap: space.sm,
	},
	label: {
		...type.button,
		color: colors.onPrimary,
		textAlign: 'center',
	},
	labelDisabled: {
		color: colors.disabledText,
	},
});

export default PrimaryButton;
